import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgPolicy } from '@newgenus/common';
import { Component, Inject, OnInit, Optional, inject } from '@angular/core';
import { OrgViewToggleComponent, OrgListViewService } from '../../shared/org-view-toggle';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../material.module';
import { OrgPolicyDetailsDialogComponent } from '../org-policy-details-dialog';
import { OrgPolicyDialogComponent } from '../org-policy-dialog/org-policy-dialog';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent, PositionSearchResult } from '../../shared/org-search-bar/org-search-bar.component';
import { fuzzyMatch } from '../../shared/org-filter-utils';
import { restrictToKeys } from '../../shared/only-keys';

export interface PoliciesListDialogData {
    organization: Organization;
    positionFilter?: string;
    /** Only these items (a staff member's dashboard tile, ISS-022); absent = all. */
    onlyKeys?: string[];
}

interface PolicyDisplayCard {
    key: string;
    name: string;
    version: number;
    revisionCount: number;
    userCount: number;
    positionCount: number;
    hatCount: number;
    partsCount: number;
    status: string;
    requiresAcknowledgment: boolean;
    isFullyAcknowledged: boolean;
    positionNames: string[];
    hatNames: string[];
    userNames: string[];
    orgPolicy: OrgPolicy;
    isOnPost: boolean;
}

@Component({
    selector: 'shared-org-policies-list-dialog',
    imports: [
        MaterialModule,
        CommonModule,
        FormsModule,
        OrgFilterBarComponent,
        OrgSearchBarComponent,
        OrgViewToggleComponent,
    ],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2">policy</mat-icon>
            Organization Policies ({{ filteredPolicies.length }} of {{ allPolicies.length }})

            <!-- Policy Explorer button -->
            <button
                mat-icon-button
                class="explorer-button"
                (click)="openPolicyExplorer()"
                matTooltip="Open Policy Explorer - View policies by status, category, or target"
            >
                <mat-icon>account_tree</mat-icon>
            </button>

            <shared-org-view-toggle></shared-org-view-toggle>
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content" [class.ng-list-view]="(listView.mode$ | async) === 'list'">
            <!-- Search Bar -->
            <shared-org-search-bar
                searchLabel="Search policies"
                searchPlaceholder="Search by name, hat, staff member, org part..."
                [showPositionSearch]="true"
                [positionNames]="allPositionNames"
                [initialPositionTerm]="positionSearchTerm"
                (searchChanged)="onSearchChanged($event)"
                (positionSearchChanged)="onPositionSearchChanged($event)"
            >
            </shared-org-search-bar>

            <!-- Filters -->
            <shared-org-filter-bar
                [filters]="filterConfig"
                [rightFilters]="rightFilterConfig"
                activeColor="#00897b"
                activeDarkColor="#4db6ac"
                [totalCount]="allPolicies.length"
                (filtersChanged)="onFiltersChanged($event)"
            >
            </shared-org-filter-bar>

            <!-- Policy card template (reused in both views) -->
            <ng-template #policyCardTpl let-policy>
                <mat-card class="policy-card" (dblclick)="onPolicyDoubleClick(policy)">
                    <!-- Top section: icon + name + version -->
                    <div class="card-identity">
                        <div
                            class="entity-avatar"
                            [ngStyle]="{
                                background:
                                    'linear-gradient(135deg, ' +
                                    entityColors.getColor('policies') +
                                    ' 0%, ' +
                                    entityColors.getColor('policies') +
                                    'cc 100%)',
                            }"
                        >
                            <mat-icon class="entity-icon">policy</mat-icon>
                        </div>
                        <div class="identity-text">
                            <span class="entity-name">{{ policy.name | titlecase }}</span>
                            <span class="entity-subtitle">Version {{ policy.version }}</span>
                        </div>
                    </div>

                    <!-- Footer: badges left, action buttons right -->
                    <div class="card-footer">
                        <div class="badges-row">
                            <span
                                class="badge"
                                matTooltip="Revisions"
                                [ngStyle]="
                                    policy.revisionCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('policies'),
                                              color: entityColors.getColor('policies'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">history</mat-icon>
                                {{ policy.revisionCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Staff assigned"
                                [ngStyle]="
                                    policy.userCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('users'),
                                              color: entityColors.getColor('users'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">people</mat-icon>
                                {{ policy.userCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Org Parts"
                                [ngStyle]="
                                    policy.positionCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('positions'),
                                              color: entityColors.getColor('positions'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">business</mat-icon>
                                {{ policy.positionCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Hats using this policy"
                                [ngStyle]="
                                    policy.hatCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('hats'),
                                              color: entityColors.getColor('hats'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">school</mat-icon>
                                {{ policy.hatCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Organization parts targeted"
                                [ngStyle]="
                                    policy.partsCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('positions'),
                                              color: entityColors.getColor('positions'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">account_tree</mat-icon>
                                {{ policy.partsCount }}
                            </span>
                        </div>
                        <div class="card-actions">
                            <button
                                mat-icon-button
                                matTooltip="View Details"
                                color="primary"
                                (click)="onViewPolicy(policy); $event.stopPropagation()"
                            >
                                <mat-icon>visibility</mat-icon>
                            </button>
                            <button
                                mat-icon-button
                                matTooltip="Edit Policy"
                                color="accent"
                                (click)="onEditPolicy(policy); $event.stopPropagation()"
                            >
                                <mat-icon>edit</mat-icon>
                            </button>
                        </div>
                    </div>
                </mat-card>
            </ng-template>

            <!-- Grouped by Position view -->
            <ng-container *ngIf="groupByPosition">
                <div *ngFor="let group of positionGroups" class="position-group">
                    <div
                        class="group-header"
                        [ngStyle]="{ 'border-left-color': entityColors.getColor('positions') }"
                        (click)="togglePositionCollapse(group.position)"
                    >
                        <mat-icon class="collapse-icon" [class.collapsed]="collapsedPositions.has(group.position)"
                            >expand_more</mat-icon
                        >
                        <mat-icon [ngStyle]="{ color: entityColors.getColor('positions') }">work</mat-icon>
                        <span class="group-name">{{ group.position }}</span>
                        <span class="group-count">({{ group.policies.length }})</span>
                    </div>
                    <div class="policies-grid" *ngIf="!collapsedPositions.has(group.position)">
                        <ng-container *ngFor="let policy of group.policies">
                            <ng-container
                                *ngTemplateOutlet="policyCardTpl; context: { $implicit: policy }"
                            ></ng-container>
                        </ng-container>
                    </div>
                </div>
                <div *ngIf="positionGroups.length === 0" class="empty-state">
                    <mat-icon class="empty-icon">account_tree</mat-icon>
                    <p>No org part groups to display</p>
                </div>
            </ng-container>

            <!-- Normal (ungrouped) view -->
            <div class="policies-grid" *ngIf="!groupByPosition && filteredPolicies.length > 0">
                <ng-container *ngFor="let policy of filteredPolicies">
                    <ng-container *ngTemplateOutlet="policyCardTpl; context: { $implicit: policy }"></ng-container>
                </ng-container>
            </div>

            <!-- Empty State -->
            <div *ngIf="!groupByPosition && filteredPolicies.length === 0" class="empty-state">
                <mat-icon class="empty-icon">policy</mat-icon>
                <p *ngIf="searchTerm">No policies found matching "{{ searchTerm }}"</p>
                <p *ngIf="!searchTerm && hasActiveFilter">No policies match the active filters</p>
                <p *ngIf="!searchTerm && !hasActiveFilter">No policies in this organization</p>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
            }

            .close-button {
                margin-left: 4px;
            }

            .explorer-button {
                color: var(--entity-color-policies, #00897b) !important;
            }

            .explorer-button:hover {
                background-color: rgba(0, 137, 123, 0.1);
            }

            .dialog-content {
                padding: 16px 24px;
                min-height: 400px;
                max-height: 70vh;
                overflow-y: auto;
            }

            .policies-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 16px;
            }

            .policy-card {
                cursor: pointer;
                transition:
                    transform 0.2s,
                    box-shadow 0.2s,
                    border-color 0.2s;
                border: 1px solid rgba(0, 0, 0, 0.08);
                display: flex;
                flex-direction: column;
                padding: 12px 16px;
                background-color: #f5f5f5;
            }

            .policy-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border-color: rgba(0, 0, 0, 0.25);
            }

            .card-identity {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 12px;
            }

            .entity-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }

            .entity-icon {
                color: white;
                font-size: 22px;
                width: 22px;
                height: 22px;
            }

            .identity-text {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                min-width: 0;
                flex: 1;
            }

            .entity-name {
                font-size: 15px;
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .entity-subtitle {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.55);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .card-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                border-top: 1px solid rgba(0, 0, 0, 0.06);
                padding-top: 8px;
            }

            .badges-row {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                min-width: 0;
            }

            .card-actions {
                display: flex;
                flex-shrink: 0;
                align-items: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button .mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }

            .badge {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                padding: 2px 6px;
                border-radius: 12px;
                font-size: 12px;
                background-color: rgba(0, 0, 0, 0.06);
                color: rgba(0, 0, 0, 0.5);
            }

            .badge-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }

            /* Position group layout */
            .position-group {
                margin-bottom: 24px;
            }

            .group-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                margin-bottom: 12px;
                border-left: 3px solid;
                background: rgba(0, 0, 0, 0.03);
                border-radius: 0 4px 4px 0;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                user-select: none;
            }

            .group-header:hover {
                background: rgba(0, 0, 0, 0.06);
            }

            .collapse-icon {
                transition: transform 0.2s;
                color: rgba(0, 0, 0, 0.4);
                font-size: 20px;
                width: 20px;
                height: 20px;
            }

            .collapse-icon.collapsed {
                transform: rotate(-90deg);
            }

            .group-name {
                font-weight: 600;
            }

            .group-count {
                color: rgba(0, 0, 0, 0.45);
                font-weight: 400;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                color: rgba(0, 0, 0, 0.54);
            }

            .empty-icon {
                font-size: 64px;
                width: 64px;
                height: 64px;
                margin-bottom: 16px;
            }

            .mr-2 {
                margin-right: 8px;
            }

            /* Dark theme overrides */
            :host-context(.dark-theme) .policy-card {
                background-color: #383838;
                border-color: rgba(255, 255, 255, 0.1);
            }
            :host-context(.dark-theme) .policy-card:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                border-color: rgba(255, 255, 255, 0.2);
            }
            :host-context(.dark-theme) .entity-subtitle {
                color: rgba(255, 255, 255, 0.55);
            }
            :host-context(.dark-theme) .card-footer {
                border-top-color: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .badge {
                background-color: rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.5);
            }
            :host-context(.dark-theme) .group-header {
                background: rgba(255, 255, 255, 0.04);
            }
            :host-context(.dark-theme) .group-header:hover {
                background: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .collapse-icon {
                color: rgba(255, 255, 255, 0.4);
            }
            :host-context(.dark-theme) .group-count {
                color: rgba(255, 255, 255, 0.45);
            }
            :host-context(.dark-theme) .empty-state {
                color: rgba(255, 255, 255, 0.54);
            }

            /* Phones: full-screen dialog (global .ob-list-dialog) → tighten chrome and
           drop the 280px-min grid to a single full-width column. */
            @media (max-width: 767.98px) {
                .dialog-header {
                    padding: 12px 16px;
                    font-size: 18px;
                }
                .dialog-content {
                    padding: 12px 16px;
                    min-height: 0;
                }
                /* minmax(0,1fr) (not 1fr) removes the implicit min-content floor so the
               card actually shrinks to the viewport instead of overflowing right. */
                .policies-grid {
                    grid-template-columns: minmax(0, 1fr);
                    gap: 12px;
                }
                .policies-grid > * {
                    min-width: 0;
                    max-width: 100%;
                }
                .card-identity,
                .identity-text {
                    min-width: 0;
                }
                /* Badges take a full row (wrapping internally), actions drop below. */
                .card-footer {
                    flex-wrap: wrap;
                }
                .badges-row {
                    flex: 1 1 100%;
                }
                .card-actions {
                    margin-left: auto;
                }
            }
        `,
    ],
})
export class OrgPoliciesListDialogComponent implements OnInit {
    public entityColors = inject(OrgEntityColorsService);
    public listView = inject(OrgListViewService);
    public allPolicies: PolicyDisplayCard[] = [];
    public filteredPolicies: PolicyDisplayCard[] = [];
    public searchTerm = '';
    public positionSearchTerm = '';
    public matchedPositionNames: string[] = [];

    // Filters
    public publishedFilter = false;
    public draftFilter = false;
    public noHatsFilter = false;
    public noUsersFilter = false;
    public requiresAckFilter = false;
    public publishedCount = 0;
    public draftCount = 0;
    public noHatsCount = 0;
    public noUsersCount = 0;
    public requiresAckCount = 0;
    public fullyAckFilter = false;
    public fullyAckCount = 0;
    public postsFilter = false;
    public postsCount = 0;
    public allPositionNames: string[] = [];
    public filterConfig: OrgFilterConfig[] = [];
    public rightFilterConfig: OrgFilterConfig[] = [];

    // Position grouping
    public groupByPosition = false;
    public positionGroups: { position: string; policies: PolicyDisplayCard[] }[] = [];
    public collapsedPositions = new Set<string>();

    constructor(
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<OrgPoliciesListDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PoliciesListDialogData,
        @Optional() private orgDialogService: OrgDialogService
    ) {}

    public ngOnInit(): void {
        console.log('OrgPoliciesListDialogComponent > ngOnInit > data:', this.data);
        if (this.data?.positionFilter) {
            this.positionSearchTerm = this.data.positionFilter;
        }
        this.loadPolicies();
    }

    private loadPolicies(): void {
        const org = this.data?.organization;
        if (!org) {
            console.warn('No organization data provided');
            return;
        }

        const orgPolicies = org.orgPolicies;
        const orgHats = org.orgHats;

        if (!orgPolicies) {
            console.warn('No policies in organization');
            return;
        }

        // Get all policy keys
        const policyKeys = orgPolicies.policiesKeys || [];

        console.log('loadPolicies > policyKeys:', policyKeys);

        // Build a map of which hats use which policies (legacy model)
        const hatsByPolicyKey = new Map<string, string[]>();

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey];
                if (hat && typeof hat === 'object' && 'policyKeys' in hat) {
                    const hatPolicyKeys = (hat as { policyKeys?: Record<string, string> }).policyKeys || {};

                    for (const policyKey of Object.keys(hatPolicyKeys)) {
                        if (!hatsByPolicyKey.has(policyKey)) {
                            hatsByPolicyKey.set(policyKey, []);
                        }
                        const hatsArray = hatsByPolicyKey.get(policyKey);
                        if (hatsArray) {
                            hatsArray.push(hatKey);
                        }
                    }
                }
            }
        }

        this.allPolicies = restrictToKeys(policyKeys as string[], this.data?.onlyKeys, (k) => k)
            .map((policyKey: string) => {
                const orgPolicy = orgPolicies[policyKey] as OrgPolicy;

                if (!orgPolicy || typeof orgPolicy !== 'object') {
                    return null;
                }

                const revisionCount = orgPolicy?.revisions?.length || 0;

                // Calculate hat count from multiple sources:
                // 1. resolvedHatCount on policy (preferred)
                // 2. resolvedHatKeys on policy
                // 3. Legacy: hats that have this policy in their policyKeys
                let hatCount = 0;
                let hatKeys: string[] = [];

                if (orgPolicy?.targets?.resolvedHatCount) {
                    hatCount = orgPolicy.targets.resolvedHatCount;
                    hatKeys = orgPolicy.targets?.resolvedHatKeys || [];
                } else if (orgPolicy?.targets?.resolvedHatKeys?.length) {
                    hatCount = orgPolicy.targets.resolvedHatKeys.length;
                    hatKeys = orgPolicy.targets.resolvedHatKeys;
                } else {
                    hatKeys = hatsByPolicyKey.get(policyKey) || [];
                    hatCount = hatKeys.length;
                }

                // Calculate user count and position count from the hats
                let userCount = 0;
                const positionKeys = new Set<string>();

                for (const hatKey of hatKeys) {
                    const hat = orgHats?.[hatKey];
                    if (hat && typeof hat === 'object') {
                        const hatObj = hat as any;
                        // Count users assigned to this hat
                        const hatUsers = hatObj.assignedToArray?.length || hatObj.userKeys?.length || 0;
                        userCount += hatUsers;

                        // Count positions from this hat
                        const positions = hatObj.positionArray || [];
                        for (const posKey of positions) {
                            positionKeys.add(posKey);
                        }

                        // Also check positions array (object array)
                        const posArray = hatObj.positions || [];
                        for (const pos of posArray) {
                            const posKey = pos?.key || pos?.partKey;
                            if (posKey) {
                                positionKeys.add(posKey);
                            }
                        }

                        // Check orgPosition if it exists
                        if (hatObj.orgPosition?.key || hatObj.orgPosition?.partKey) {
                            positionKeys.add(hatObj.orgPosition.key || hatObj.orgPosition.partKey);
                        }
                    }
                }

                // Calculate parts count from policy targets
                const partsCount = orgPolicy?.targets?.parts?.length || 0;

                return {
                    key: policyKey,
                    name: orgPolicy?.name || policyKey,
                    version: orgPolicy?.version || 1,
                    revisionCount,
                    userCount,
                    positionCount: positionKeys.size,
                    hatCount,
                    partsCount,
                    status: orgPolicy?.status || 'draft',
                    requiresAcknowledgment: orgPolicy?.requiresAcknowledgment || false,
                    isFullyAcknowledged: !!(
                        orgPolicy?.requiresAcknowledgment &&
                        orgPolicy?.acknowledgmentStats?.completionPercentage === 100
                    ),
                    positionNames: this.resolvePositionNames(positionKeys, org),
                    hatNames: this.resolveHatNames(hatKeys, org),
                    userNames: this.resolveUserNamesFromHats(hatKeys, org),
                    orgPolicy,
                    isOnPost: this.isPolicyLinkedToPost(hatKeys, org),
                } as PolicyDisplayCard;
            })
            .filter((policy): policy is PolicyDisplayCard => policy !== null);

        // Sort by name
        this.allPolicies.sort((a, b) => a.name.localeCompare(b.name));
        this.computeFilterCounts();
        this.allPositionNames = Array.from(new Set(this.allPolicies.flatMap((p) => p.positionNames))).sort();
        this.buildFilterConfigs();
        this.filterPolicies();

        console.log('loadPolicies > allPolicies:', this.allPolicies);
    }

    private computeFilterCounts(): void {
        this.publishedCount = this.allPolicies.filter((p) => p.status === 'published').length;
        this.draftCount = this.allPolicies.filter((p) => p.status === 'draft').length;
        this.noHatsCount = this.allPolicies.filter((p) => p.hatCount === 0).length;
        this.noUsersCount = this.allPolicies.filter((p) => p.userCount === 0).length;
        this.requiresAckCount = this.allPolicies.filter((p) => p.requiresAcknowledgment).length;
        this.fullyAckCount = this.allPolicies.filter((p) => p.isFullyAcknowledged).length;
        this.postsCount = this.allPolicies.filter((p) => p.isOnPost).length;
        this.buildFilterConfigs();
    }

    public get hasActiveFilter(): boolean {
        return (
            this.publishedFilter ||
            this.draftFilter ||
            this.noHatsFilter ||
            this.noUsersFilter ||
            this.requiresAckFilter ||
            this.fullyAckFilter ||
            this.postsFilter
        );
    }

    public filterPolicies(): void {
        let result = [...this.allPolicies];

        // Text search across name, hats, users, positions
        if (this.searchTerm.trim()) {
            const term = this.searchTerm.toLowerCase().trim();
            result = result.filter(
                (policy) =>
                    policy.name.toLowerCase().includes(term) ||
                    policy.hatNames.some((h) => h.toLowerCase().includes(term)) ||
                    policy.userNames.some((u) => u.toLowerCase().includes(term)) ||
                    policy.positionNames.some((p) => p.toLowerCase().includes(term))
            );
        }

        // Position search (fuzzy) — handled by shared search bar, just apply the filter
        if (this.positionSearchTerm.trim()) {
            result = result.filter((policy) =>
                policy.positionNames.some((pos) => fuzzyMatch(pos, this.positionSearchTerm))
            );
        }

        // Toggle filters
        if (this.publishedFilter) {
            result = result.filter((p) => p.status === 'published');
        }
        if (this.draftFilter) {
            result = result.filter((p) => p.status === 'draft');
        }
        if (this.noHatsFilter) {
            result = result.filter((p) => p.hatCount === 0);
        }
        if (this.noUsersFilter) {
            result = result.filter((p) => p.userCount === 0);
        }
        if (this.requiresAckFilter) {
            result = result.filter((p) => p.requiresAcknowledgment);
        }
        if (this.fullyAckFilter) {
            result = result.filter((p) => p.isFullyAcknowledged);
        }
        if (this.postsFilter) {
            result = result.filter((p) => p.isOnPost);
        }

        this.filteredPolicies = result;

        if (this.groupByPosition) {
            this.buildPositionGroups();
        }
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.filterPolicies();
    }

    public onPositionSearchChanged(result: PositionSearchResult): void {
        this.positionSearchTerm = result.term;
        this.matchedPositionNames = result.matchedNames;
        this.filterPolicies();
    }

    public onFiltersChanged(activeFilters: Set<string>): void {
        this.publishedFilter = activeFilters.has('published');
        this.draftFilter = activeFilters.has('draft');
        this.noHatsFilter = activeFilters.has('noHats');
        this.noUsersFilter = activeFilters.has('noUsers');
        this.requiresAckFilter = activeFilters.has('requiresAck');
        this.fullyAckFilter = activeFilters.has('fullyAck');
        this.postsFilter = activeFilters.has('posts');

        const newGroupByPosition = activeFilters.has('groupByPosition');
        if (newGroupByPosition !== this.groupByPosition) {
            this.groupByPosition = newGroupByPosition;
            if (!this.groupByPosition) {
                this.collapsedPositions.clear();
            }
        }

        this.filterPolicies();
    }

    private buildFilterConfigs(): void {
        this.filterConfig = [
            { key: 'published', label: 'Published', icon: 'check_circle', count: this.publishedCount },
            { key: 'draft', label: 'Draft', icon: 'edit_note', count: this.draftCount },
            { key: 'noHats', label: 'No Hats', icon: 'school', count: this.noHatsCount },
            { key: 'noUsers', label: 'No Staff', icon: 'person_off', count: this.noUsersCount },
            { key: 'requiresAck', label: 'Requires Ack', icon: 'pending_actions', count: this.requiresAckCount },
            { key: 'fullyAck', label: 'Fully Ack', icon: 'verified', count: this.fullyAckCount },
            { key: 'posts', label: 'Posts', icon: 'work', count: this.postsCount },
        ];
        this.rightFilterConfig = [{ key: 'groupByPosition', label: 'By Org Part', icon: 'account_tree' }];
    }

    public togglePositionCollapse(position: string): void {
        if (this.collapsedPositions.has(position)) {
            this.collapsedPositions.delete(position);
        } else {
            this.collapsedPositions.add(position);
        }
    }

    private buildPositionGroups(): void {
        const groupMap = new Map<string, PolicyDisplayCard[]>();

        this.filteredPolicies.forEach((policy) => {
            if (policy.positionNames.length === 0) {
                const key = 'No Org Part';
                if (!groupMap.has(key)) groupMap.set(key, []);
                groupMap.get(key)?.push(policy);
            } else {
                policy.positionNames.forEach((pos) => {
                    if (!groupMap.has(pos)) groupMap.set(pos, []);
                    groupMap.get(pos)?.push(policy);
                });
            }
        });

        this.positionGroups = Array.from(groupMap.entries())
            .map(([position, policies]) => ({ position, policies }))
            .sort((a, b) => {
                if (a.position === 'No Org Part') return 1;
                if (b.position === 'No Org Part') return -1;
                return a.position.localeCompare(b.position);
            });

        // Collapse all by default on first build
        if (this.collapsedPositions.size === 0 && this.positionGroups.length > 0) {
            this.positionGroups.forEach((g) => this.collapsedPositions.add(g.position));
        }
    }

    private isPolicyLinkedToPost(hatKeys: string[], org: Organization): boolean {
        const orgHats = org?.orgHats || ({} as any);
        const orgParts = org.orgParts || [];
        return hatKeys.some((hatKey) => {
            const hat = orgHats[hatKey];
            if (!hat || typeof hat !== 'object') return false;
            const positionArray: string[] = hat.positionArray || [];
            return positionArray.some((partKey: string) => {
                const part = orgParts.find((p: any) => p.key === partKey || p.partKey === partKey);
                if (!part) return false;
                const partType = part.partType ? part.partType.toLowerCase() : '';
                if (partType === 'post') return true;
                if (!partType && (part.depth || 0) >= 6) return true;
                return false;
            });
        });
    }

    private resolvePositionNames(positionKeys: Set<string>, org: Organization): string[] {
        const names: string[] = [];
        const orgParts = org.orgParts || [];

        positionKeys.forEach((partKey) => {
            const part = orgParts.find((p: any) => p.key === partKey || p.partKey === partKey);
            if (part?.name && !names.includes(part.name)) {
                names.push(part.name);
            }
        });

        return names;
    }

    private resolveHatNames(hatKeys: string[], org: Organization): string[] {
        const orgHats = org?.orgHats || ({} as any);
        return hatKeys
            .map((k) => {
                const hat = orgHats[k];
                return hat && typeof hat === 'object' ? hat.name || '' : '';
            })
            .filter((n: string) => !!n);
    }

    private resolveUserNamesFromHats(hatKeys: string[], org: Organization): string[] {
        const orgHats = org?.orgHats || ({} as any);
        const orgUsers = org?.orgUsers;
        if (!orgUsers) return [];
        const userNames = new Set<string>();
        for (const hatKey of hatKeys) {
            const hat = orgHats[hatKey];
            if (!hat || typeof hat !== 'object') continue;
            const userKeys: string[] = hat.assignedToArray || hat.userKeys || [];
            for (const uid of userKeys) {
                if (orgUsers[uid]) {
                    userNames.add(orgUsers[uid].name || orgUsers[uid].userName || '');
                }
            }
        }
        return Array.from(userNames).filter((n) => !!n);
    }

    public onPolicyDoubleClick(policy: PolicyDisplayCard): void {
        this.openPolicyDetails(policy);
    }

    public onViewPolicy(policy: PolicyDisplayCard): void {
        this.openPolicyDetails(policy);
    }

    public onEditPolicy(policy: PolicyDisplayCard): void {
        // Close this list dialog and open the edit dialog with the policy
        this.dialogRef.close();

        this.dialog.open(OrgPolicyDialogComponent, {
            width: '95vw',
            height: '95vh',
            maxWidth: '1800px',
            maxHeight: '1000px',
            data: {
                organization: this.data.organization,
                goToNewTab: true, // Open on editor tab
                policyToEdit: policy.orgPolicy, // Pass the policy to edit
                apiBaseUrl: this.orgDialogService?.apiBaseUrl, // Enable backend API for publish
            },
            panelClass: 'policy-editor-dialog',
        });
    }

    private openPolicyDetails(policy: PolicyDisplayCard): void {
        console.log('Opening policy details for:', policy.name);

        this.dialog.open(OrgPolicyDetailsDialogComponent, {
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            data: {
                policy: policy.orgPolicy,
                policyKey: policy.key,
                organization: this.data.organization,
            },
            panelClass: 'policy-details-dialog',
        });
    }

    public openPolicyExplorer(): void {
        import('../org-policy-explorer-dialog/org-policy-explorer-dialog').then((m) => {
            this.dialog.open(m.OrgPolicyExplorerDialogComponent, {
                width: '90%',
                height: '90vh',
                maxWidth: '1400px',
                maxHeight: '900px',
                data: {
                    organization: this.data.organization,
                },
                panelClass: ['policy-explorer-dialog', 'ob-list-dialog'],
            });
        });
    }
}
